import { useMemo } from 'react';

import { useSessionForecastDataQuery } from 'src/services/api/getOpenSessionForecastData';
import forecastService from 'src/services/forecast/forecastService';
import { useBoundStore } from 'src/store/store';
import type { ForecastDataPoint } from 'src/types/domain/ForecastDataPoint';

export const useForecastPaginatedQuery = (
  sessionId: string,
  page: number = 1,
) => {
  const {
    data: queryData,
    isLoading,
    error,
  } = useSessionForecastDataQuery(sessionId, page);
  const { forecastData } = useBoundStore((state) => state);

  const sortPerDate = (a: ForecastDataPoint, b: ForecastDataPoint) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.month - b.month;
  };

  const data = useMemo(() => {
    if (!queryData) return [];

    if (!queryData.sku) {
      return queryData.data.sort(sortPerDate);
    }

    const storeData =
      forecastData[forecastService.getStoreKey(queryData.sku, sessionId)];
    if (storeData) {
      return storeData.sort(sortPerDate);
    }

    return queryData.data.sort(sortPerDate);
  }, [forecastData, queryData, sessionId]);

  return {
    data,
    queryData: queryData?.data,
    sku: queryData?.sku,
    validatedAt: queryData?.validated_at,
    validatedNumber: queryData?.validated_number ?? 0,
    totalProducts: queryData?.total_products,
    currentPage: queryData?.current_page,
    totalPages: queryData?.total_pages,
    hasNext: queryData?.has_next,
    hasPrevious: queryData?.has_previous,
    sessionId: queryData?.session_id,
    sessionName: queryData?.session_name,
    page: queryData?.page,
    isLoading,
    error,
  };
};
